import { useCallback, useEffect, useState } from "react";
import { Truck, CheckCircle2, AlertTriangle, Save } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { apiGetShippingSettings, apiSaveShippingSettings, errMsg } from "../adminApi";

const FIELDS = [
  { key: "ragione_sociale", label: "Ragione sociale", placeholder: "NOVA STRATEGIE SRLS", full: true },
  { key: "indirizzo", label: "Indirizzo", placeholder: "Via e numero civico", full: true },
  { key: "cap", label: "CAP", placeholder: "76011" },
  { key: "citta", label: "Città", placeholder: "Bisceglie" },
  { key: "provincia", label: "Provincia (sigla)", placeholder: "BT" },
  { key: "telefono", label: "Telefono", placeholder: "+39 389 367 0190" },
  { key: "email", label: "Email", placeholder: "info@rinovotech.it" },
  { key: "peso_default", label: "Peso pacco predefinito (kg)", placeholder: "1.5", type: "number" },
  { key: "tracking_url_template", label: "Template URL tracking", placeholder: "https://www.gls-italy.com/?match={tracking}", full: true },
];

export default function ShippingSettings() {
  const [form, setForm] = useState(null);
  const [gls, setGls] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiGetShippingSettings()
      .then((d) => { setForm(d.sender); setGls(d.gls); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, peso_default: parseFloat(form.peso_default) || 1.0 };
      const res = await apiSaveShippingSettings(payload);
      setForm(res.sender);
      toast.success("Impostazioni spedizione salvate");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex max-w-3xl flex-col gap-6" data-testid="shipping-settings-page">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface3 text-lime">
          <Truck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight sm:text-3xl">Spedizioni GLS</h1>
          <p className="mt-1 text-sm text-steel">Dati mittente usati per creare le spedizioni GLS dagli ordini.</p>
        </div>
      </div>

      {/* Stato connessione GLS */}
      {gls && (
        <div
          className={`flex items-start gap-3 rounded-xl border p-4 ${
            gls.configured
              ? "border-lime/40 bg-lime/10"
              : "border-carrot/40 bg-carrot/10"
          }`}
          data-testid="gls-status-banner"
        >
          {gls.configured ? (
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-lime" />
          ) : (
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-carrot" />
          )}
          <div className="text-sm">
            {gls.configured ? (
              <p className="text-chalk">
                Connessione GLS attiva — ambiente <strong>{gls.env === "production" ? "produzione" : "test"}</strong>.
              </p>
            ) : (
              <>
                <p className="font-medium text-chalk">Connessione GLS non ancora completa.</p>
                <p className="mt-1 text-steel">
                  Stato chiavi: chiave privata {gls.has_api_key ? "presente" : "mancante"},
                  chiave pubblica {gls.has_public_key ? "presente" : "mancante"},
                  URL base {gls.has_base_url ? "presente" : "mancante"}.
                  Completa i dati mancanti nei Secrets per abilitare la creazione delle spedizioni.
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <Card className="border-line bg-surface2">
        <CardContent className="p-5">
          {!form ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <div key={f.key} className={`flex flex-col gap-1.5 ${f.full ? "sm:col-span-2" : ""}`}>
                  <Label htmlFor={`ship-${f.key}`}>{f.label}</Label>
                  <Input
                    id={`ship-${f.key}`}
                    type={f.type || "text"}
                    step={f.type === "number" ? "0.1" : undefined}
                    value={form[f.key] ?? ""}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="bg-surface"
                    data-testid={`ship-field-${f.key}`}
                  />
                  {f.key === "tracking_url_template" && (
                    <p className="text-xs text-tertiary">
                      Usa <code>{"{tracking}"}</code> dove va inserito il numero di tracking.
                    </p>
                  )}
                </div>
              ))}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="ship-costo-default">Costo spedizione predefinito (€)</Label>
                <Input
                  id="ship-costo-default"
                  type="number"
                  step="0.01"
                  value={form.costo_spedizione_default_cents != null ? form.costo_spedizione_default_cents / 100 : ""}
                  placeholder="es. 6.00"
                  onChange={(e) => set("costo_spedizione_default_cents", e.target.value === "" ? 0 : Math.round(Number(e.target.value) * 100))}
                  className="bg-surface"
                  data-testid="ship-field-costo-default"
                />
                <p className="text-xs text-tertiary">Usato come costo reale di spedizione finché GLS non restituisce il costo effettivo.</p>
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <Button variant="lime" onClick={save} disabled={saving || !form} data-testid="ship-settings-save">
              <Save className="h-4 w-4" /> Salva impostazioni
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
